import { useState, useRef, useEffect, useCallback } from "react";
import { MapPin, Heart, AlertTriangle, Search, Dog, ClipboardEdit, BadgeCheck, PlusCircle, ArrowRight, ChevronDown, X, Building2, BarChart3 } from "lucide-react";
import type { Shelter } from "@/types/shelter";
import { isUrgentNeedsActive } from "@/types/shelter";
import heroBg640 from "@/assets/hero-bg-640.webp";
import heroBg1024 from "@/assets/hero-bg-1024.webp";
import heroBg from "@/assets/hero-bg.webp";
import { Button } from "@/components/ui/button";
import { ShelterSearchDialog } from "@/components/ShelterSearchDialog";
import { useCountUp } from "@/hooks/useCountUp";

interface HeroSectionProps {
  shelters: Shelter[];
  onScrollToMap: () => void;
  onShelterSelect: (shelter: Shelter) => void;
}

const pluralCities = (n: number): string => {
  if (n === 1) return "miasto";
  const lastTwo = n % 100;
  const lastOne = n % 10;
  if (lastTwo >= 12 && lastTwo <= 14) return "miast";
  if (lastOne >= 2 && lastOne <= 4) return "miasta";
  return "miast";
};

const pluralShelters = (n: number): string => {
  if (n === 1) return "schronisko";
  const lastTwo = n % 100;
  const lastOne = n % 10;
  if (lastTwo >= 12 && lastTwo <= 14) return "schronisk";
  if (lastOne >= 2 && lastOne <= 4) return "schroniska";
  return "schronisk";
};

const pluralUrgent = (n: number): string => {
  if (n === 1) return "pilna potrzeba";
  const lastTwo = n % 100;
  const lastOne = n % 10;
  if (lastTwo >= 12 && lastTwo <= 14) return "pilnych potrzeb";
  if (lastOne >= 2 && lastOne <= 4) return "pilne potrzeby";
  return "pilnych potrzeb";
};

export const HeroSection = ({ shelters, onScrollToMap, onShelterSelect }: HeroSectionProps) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem("hero-hidden") === "true");
  const [collapsing, setCollapsing] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  const stats = {
    total: shelters.length,
    urgent: shelters.filter(isUrgentNeedsActive).length,
    verified: shelters.filter(s => s.organization_verified).length,
    cities: new Set(shelters.map(s => s.city)).size,
  };

  const animTotal = useCountUp(stats.total);
  const animUrgent = useCountUp(stats.urgent);
  const animVerified = useCountUp(stats.verified);
  const animCities = useCountUp(stats.cities);

  const ANIM_MS = 350;
  const PILL_EXIT_MS = 200;
  const [pillExiting, setPillExiting] = useState(false);

  const handleHide = useCallback((persist = true) => {
    setCollapsing(true);
    if (persist) localStorage.setItem("hero-hidden", "true");
  }, []);

  // Auto-collapse removed — hero scrolls normally

  const handleShow = useCallback(() => {
    setPillExiting(true);
    setTimeout(() => {
      setHidden(false);
      setExpanding(true);
      setPillExiting(false);
      localStorage.removeItem("hero-hidden");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setExpanding(false));
      });
    }, PILL_EXIT_MS);
  }, []);

  const handleTransitionEnd = useCallback((e: React.TransitionEvent<HTMLDivElement>) => {
    // Only react to the wrapper's own max-height transition
    if (e.target !== e.currentTarget || e.propertyName !== 'max-height') return;
    if (collapsing) {
      setHidden(true);
      setCollapsing(false);
    }
  }, [collapsing]);

  if (hidden && !collapsing) {
    return (
      <div className="relative h-0 flex justify-end px-2.5 sm:px-4 pointer-events-none z-[1100]">
        <button
          onClick={handleShow}
          aria-label="Pokaż sekcję powitalną"
          style={{ transformOrigin: 'top center' }}
          className={`pointer-events-auto group inline-flex items-center gap-1.5 sm:gap-2 pl-3 pr-2.5 sm:pl-5 sm:pr-4 pt-1 pb-1.5 sm:pt-2 sm:pb-2.5 rounded-b-xl sm:rounded-b-2xl bg-primary/90 hover:bg-primary text-[9px] sm:text-[10px] font-semibold text-primary-foreground border border-t-0 border-primary/60 shadow-md transition-colors ${pillExiting ? 'animate-slide-up-fade' : 'animate-slide-down-fade'}`}
        >
          <span className="leading-none">Rozwiń</span>
          <ChevronDown className="w-2.5 h-2.5 sm:w-3 sm:h-3 transition-transform group-hover:translate-y-0.5" />
        </button>
      </div>
    );
  }

  const isCollapsed = collapsing || expanding;

  return (
    <div
      ref={heroRef}
      className="overflow-hidden"
      onTransitionEnd={handleTransitionEnd}
      style={{
        maxHeight: isCollapsed ? '0px' : 'calc(100dvh - 56px)',
        opacity: isCollapsed ? 0 : 1,
        transform: isCollapsed ? 'translateY(-8px)' : 'translateY(0)',
        transition: `max-height ${ANIM_MS}ms ease-out, opacity ${ANIM_MS}ms ease-out, transform ${ANIM_MS}ms ease-out`,
      }}
    >
      <section data-hero-section className="border-b border-border overflow-hidden relative min-h-[calc(100dvh-56px)] flex flex-col">
        <img 
          src={heroBg} 
          srcSet={`${heroBg640} 640w, ${heroBg1024} 1024w, ${heroBg} 1536w`}
          sizes="100vw"
          width={1536}
          height={1024}
          alt="" 
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        {/* Mesh gradient + soft background tone */}
        <div className="absolute inset-0 bg-background/75" />
        <div className="absolute inset-0 bg-hero-mesh" />
        {/* Animated glow blobs */}
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-primary/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-20 w-[460px] h-[460px] rounded-full bg-secondary/15 blur-3xl pointer-events-none" />

        {/* Hide button - sticks to top edge, matches "Rozwiń" pill style */}
        <button
          onClick={() => handleHide()}
          aria-label="Ukryj sekcję powitalną"
          className={`group absolute top-0 right-2.5 sm:right-4 inline-flex items-center gap-1.5 sm:gap-2 pl-3 pr-2.5 sm:pl-5 sm:pr-4 pt-1 pb-1.5 sm:pt-2 sm:pb-2.5 rounded-b-xl sm:rounded-b-2xl bg-primary/90 hover:bg-primary text-[9px] sm:text-[10px] font-semibold text-primary-foreground border border-t-0 border-primary/60 shadow-md transition-all duration-200 z-10 ${collapsing ? 'opacity-0 -translate-y-1 pointer-events-none' : 'opacity-100 translate-y-0'}`}
        >
          <span className="leading-none">Ukryj</span>
          <X className="w-2.5 h-2.5 sm:w-3 sm:h-3 transition-transform group-hover:scale-110" />
        </button>

        <div className="relative max-w-screen-lg lg:max-w-5xl xl:max-w-6xl 2xl:max-w-[90rem] mx-auto px-5 pt-3 pb-4 sm:px-4 sm:py-8 lg:py-10 xl:py-12 flex-1 flex flex-col sm:justify-center">

          <div className="text-center mb-2 sm:mb-6 lg:mb-8 mt-5 sm:mt-0 animate-fade-in">
            <div className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 mb-2 sm:mb-3 lg:mb-4 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
              </span>
              <span className="text-[10px] sm:text-xs lg:text-sm font-semibold text-primary tracking-wide">Bieżące potrzeby schronisk</span>
            </div>
            <h1 className="text-xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-extrabold text-foreground mb-1 sm:mb-3 lg:mb-4 leading-[1.1] tracking-tight">
              Pomaganie zwierzakom <br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-primary via-primary to-walking bg-clip-text text-transparent">zaczyna się tutaj</span>
              <span className="inline-block ml-1">💚</span>
            </h1>
            <p className="text-muted-foreground text-xs sm:text-base lg:text-base xl:text-lg 2xl:text-xl max-w-lg lg:max-w-2xl 2xl:max-w-3xl mx-auto">
              Społecznościowa mapa schronisk z aktualnymi potrzebami, tworzona przez wolontariuszy i schroniska
            </p>
          </div>

          {shelters.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2.5 lg:gap-3 mb-2 sm:mb-6 lg:mb-10 animate-fade-in [animation-delay:100ms] [animation-fill-mode:backwards]">
              <div className="flex items-center gap-1.5 sm:gap-2 bg-card/70 backdrop-blur-md border border-white/40 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full shadow-sm">
                <span className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-primary/15">
                  <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
                </span>
                <span className="font-extrabold text-xs sm:text-sm tabular-nums text-foreground">{animTotal}</span>
                <span className="font-semibold text-[10px] sm:text-xs text-muted-foreground">{pluralShelters(animTotal)}</span>
              </div>
              {stats.verified > 0 && (
                <div className="hidden sm:flex items-center gap-1.5 sm:gap-2 bg-card/70 backdrop-blur-md border border-white/40 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full shadow-sm">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/15">
                    <BadgeCheck className="w-3 h-3 text-primary" />
                  </span>
                  <span className="font-extrabold text-xs sm:text-sm tabular-nums text-foreground">{animVerified}</span>
                  <span className="font-semibold text-[10px] sm:text-xs text-muted-foreground">zweryfikowanych</span>
                </div>
              )}
              {stats.urgent > 0 && (
                <div className="flex items-center gap-1.5 sm:gap-2 bg-card/70 backdrop-blur-md border border-urgent/30 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full shadow-sm">
                  <span className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-urgent/15">
                    <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-urgent" />
                  </span>
                  <span className="font-extrabold text-xs sm:text-sm tabular-nums text-foreground">{animUrgent}</span>
                  <span className="font-semibold text-[10px] sm:text-xs text-muted-foreground">{pluralUrgent(animUrgent)}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 sm:gap-2 bg-card/70 backdrop-blur-md border border-white/40 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full shadow-sm">
                <span className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-primary/15">
                  <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
                </span>
                <span className="font-extrabold text-xs sm:text-sm tabular-nums text-foreground">{animCities}</span>
                <span className="font-semibold text-[10px] sm:text-xs text-muted-foreground">{pluralCities(animCities)}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 lg:gap-8 2xl:gap-12 max-w-4xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-[90rem] mx-auto w-full">
            <div className="group relative flex flex-col p-4 sm:p-7 lg:p-9 xl:p-10 2xl:p-14 rounded-2xl lg:rounded-3xl bg-card/70 backdrop-blur-xl shadow-glow-soft border border-white/50 hover:border-primary/30 hover:shadow-glow transition-all duration-300 animate-fade-in [animation-delay:200ms] [animation-fill-mode:backwards] overflow-hidden">
              <div className="absolute -top-16 -right-16 w-40 h-40 lg:w-56 lg:h-56 rounded-full bg-primary/10 blur-2xl pointer-events-none opacity-60 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-center gap-2.5 lg:gap-3 mb-2 sm:mb-4 lg:mb-6">
                <span className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 2xl:w-16 2xl:h-16 rounded-xl lg:rounded-2xl bg-primary/15 ring-1 ring-primary/20">
                  <Heart className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 2xl:w-7 2xl:h-7 text-primary" />
                </span>
                <span className="font-extrabold text-[15px] sm:text-lg lg:text-2xl 2xl:text-3xl text-foreground tracking-tight">Chcę pomóc</span>
              </div>
              <ul className="relative space-y-1.5 sm:space-y-3 lg:space-y-4 2xl:space-y-5 flex-1">
                <li className="flex items-start gap-2 lg:gap-2.5 text-[13px] sm:text-sm lg:text-base 2xl:text-lg text-muted-foreground">
                  <Search className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 2xl:w-5 2xl:h-5 text-primary mt-0.5 lg:mt-1 flex-shrink-0" />
                  <span className="font-medium">Wyszukaj schronisko w Twojej okolicy</span>
                </li>
                <li className="flex items-start gap-2 lg:gap-2.5 text-[13px] sm:text-sm lg:text-base 2xl:text-lg text-muted-foreground">
                  <AlertTriangle className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 2xl:w-5 2xl:h-5 text-primary mt-0.5 lg:mt-1 flex-shrink-0" />
                  <span className="font-medium">Sprawdź aktualne potrzeby, np. karma, koce</span>
                </li>
                <li className="flex items-start gap-2 lg:gap-2.5 text-[13px] sm:text-sm lg:text-base 2xl:text-lg text-muted-foreground">
                  <Dog className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 2xl:w-5 2xl:h-5 text-primary mt-0.5 lg:mt-1 flex-shrink-0" />
                  <span className="font-medium">Idź na spacer z psem lub zostań wolontariuszem</span>
                </li>
              </ul>
              <Button className="relative w-full mt-3 sm:mt-5 lg:mt-7 2xl:mt-10 h-9 sm:h-10 lg:h-12 2xl:h-14 text-[13px] sm:text-sm lg:text-base 2xl:text-lg bg-primary hover:bg-primary/90 text-primary-foreground shadow-glow-soft hover:shadow-glow transition-all" size="sm" onClick={() => handleHide()}>
                Zobacz, gdzie możesz pomóc
                <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 2xl:w-6 2xl:h-6 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </div>

            <div className="group relative flex flex-col p-4 sm:p-7 lg:p-9 xl:p-10 2xl:p-14 rounded-2xl lg:rounded-3xl bg-card/70 backdrop-blur-xl shadow-glow-soft border border-white/50 hover:border-primary/30 hover:shadow-glow transition-all duration-300 animate-fade-in [animation-delay:350ms] [animation-fill-mode:backwards] overflow-hidden">
              <div className="absolute -top-16 -left-16 w-40 h-40 lg:w-56 lg:h-56 rounded-full bg-secondary/10 blur-2xl pointer-events-none opacity-60 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-center gap-2.5 lg:gap-3 mb-2 sm:mb-4 lg:mb-6">
                <span className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 2xl:w-16 2xl:h-16 rounded-xl lg:rounded-2xl bg-primary/15 ring-1 ring-primary/20">
                  <ClipboardEdit className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 2xl:w-7 2xl:h-7 text-primary" />
                </span>
                <span className="font-extrabold text-[15px] sm:text-lg lg:text-2xl 2xl:text-3xl text-foreground tracking-tight">Reprezentuję schronisko</span>
              </div>
              <ul className="relative space-y-1.5 sm:space-y-3 lg:space-y-4 2xl:space-y-5 flex-1">
                <li className="flex items-start gap-2 lg:gap-2.5 text-[13px] sm:text-sm lg:text-base 2xl:text-lg text-muted-foreground">
                  <Building2 className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 2xl:w-5 2xl:h-5 text-primary mt-0.5 lg:mt-1 flex-shrink-0" />
                  <span className="font-medium">Utwórz profil organizacji</span>
                </li>
                <li className="flex items-start gap-2 lg:gap-2.5 text-[13px] sm:text-sm lg:text-base 2xl:text-lg text-muted-foreground">
                  <ClipboardEdit className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 2xl:w-5 2xl:h-5 text-primary mt-0.5 lg:mt-1 flex-shrink-0" />
                  <span className="font-medium">Samodzielnie aktualizuj potrzeby</span>
                </li>
                <li className="flex items-start gap-2 lg:gap-2.5 text-[13px] sm:text-sm lg:text-base 2xl:text-lg text-muted-foreground">
                  <BarChart3 className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 2xl:w-5 2xl:h-5 text-primary mt-0.5 lg:mt-1 flex-shrink-0" />
                  <span className="font-medium">Zobacz, ile osób odwiedza profil</span>
                </li>
              </ul>
              <Button variant="outline" className="relative w-full mt-3 sm:mt-5 lg:mt-7 2xl:mt-10 h-9 sm:h-10 lg:h-12 2xl:h-14 text-[13px] sm:text-sm lg:text-base 2xl:text-lg border-primary/30 bg-card/60 backdrop-blur-sm text-primary hover:bg-primary/10 hover:text-primary hover:border-primary/50" size="sm" onClick={() => setSearchOpen(true)}>
                Znajdź swoje schronisko
                <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 2xl:w-6 2xl:h-6 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </div>
          </div>

        </div>

        <ShelterSearchDialog open={searchOpen} onOpenChange={setSearchOpen} shelters={shelters} onShelterSelect={onShelterSelect} />
      </section>
    </div>
  );
};
